import type { FC, ReactNode } from 'react';
import { Fragment, memo, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { UsePageInfoReturnType, UsePaginationReturnType } from '@/js/libs/ui/src/V2';

import { SortableTableInfo } from 'src/components/SortableTableInfo';
import { SearchFilter } from 'src/components/TableFilters';
import { DEFAULT_INPUT_DEBOUNCE } from 'src/constants';
import type { SortProps } from 'src/hooks/useSortAndOrderState';
import { useTaskTablesContext } from '../context';
import type { Column, ColumnName } from '../types';
import {
  AssignedContactFilter,
  InvoicingAccountFilter,
  LabelFilter,
  OutcomeFilter,
  PromotionFilter,
  SalesFilter,
  SourceFilter,
  TimeFrameFilter,
  TopicFilter,
} from './filters';

export interface TableFiltersProps {
  info: UsePageInfoReturnType;
  pagination: UsePaginationReturnType;
  columns: Column[];
  sortProps: SortProps<ColumnName>;
  bulkActions?: ReactNode;
  showBulkActions?: boolean;
  isLoading: boolean;
}

export const TableFilters: FC<TableFiltersProps> = memo(function TableFilters({
  info,
  pagination,
  columns,
  sortProps,
  bulkActions,
  showBulkActions,
  isLoading,
}) {
  const filters = useTaskTablesContext('filters$');
  const multiFilter = useTaskTablesContext('multiFilter$');
  const areFiltersPreloading = useTaskTablesContext('areFiltersPreloading$');
  const { updateFilter, isFilterHidden, MultiFilter, ClearFiltersButton } = useTaskTablesContext();
  const [localSearchFilter, setLocalSearchFilter] = useState<string>(filters.search);

  const handleUpdateSearchFilterDebounced = useDebouncedCallback((value: string) => {
    updateFilter('search', value);
  }, DEFAULT_INPUT_DEBOUNCE);

  const handleSetLocalSearchFilter = useCallbackRef((value: string) => {
    setLocalSearchFilter(value);
    handleUpdateSearchFilterDebounced(value);
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalSearchFilter(filters.search);
  }, [filters.search]);

  return (
    <SortableTableInfo
      info={info}
      pagination={pagination}
      filters={
        <>
          <SearchFilter filter={localSearchFilter} onChange={handleSetLocalSearchFilter} />
          {MultiFilter}
          {multiFilter.map((filter) => (
            <Fragment key={filter}>
              {!isFilterHidden(filter, 'topic') && <TopicFilter />}
              {!isFilterHidden(filter, 'outcome') && <OutcomeFilter />}
              {!isFilterHidden(filter, 'timeFrame') && <TimeFrameFilter />}
              {!isFilterHidden(filter, 'label') && <LabelFilter />}
              {!isFilterHidden(filter, 'source') && <SourceFilter />}
              {!isFilterHidden(filter, 'sales') && <SalesFilter />}
              {!isFilterHidden(filter, 'assignedContact') && <AssignedContactFilter />}
              {!isFilterHidden(filter, 'invoicingAccount') && <InvoicingAccountFilter />}
              {!isFilterHidden(filter, 'promotion') && <PromotionFilter />}
            </Fragment>
          ))}
          {ClearFiltersButton}
        </>
      }
      filtersLoading={areFiltersPreloading}
      columns={columns}
      getSortProps={sortProps}
      bulkActions={bulkActions}
      showBulkActions={showBulkActions}
      isLoading={isLoading}
    />
  );
});
